import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Check, ArrowRight, Play, Sparkles, Store, Users, Share2, Wallet,
  Globe, HeartHandshake, TrendingUp, ShieldCheck, Link2, BadgeCheck,
} from "lucide-react";
import { toast } from "sonner";
import {
  Accordion, AccordionItem, AccordionTrigger, AccordionContent,
} from "@/components/ui/accordion";

const AVG_FEE = 7.4; // standard verified per consultation fee
const SALON_RATE = 0.075; // 7.5% salon commission

const gbp = (n) =>
  "£" + Math.round(n).toLocaleString("en-GB");

const TRUST_BADGES = [
  "Worldwide offer, minimum 3 stylists to join",
  "Earn 7.5% of every stylist consultation, automatically",
  "Zero disruption, we complement your salon, we don't replace it",
];

const STREAMS = [
  {
    icon: Users,
    title: "Your stylists",
    desc: "Register your team under your salon name. Every consultation they complete on the app pays your salon 7.5%, turning quiet chairs and downtime into recurring digital revenue.",
  },
  {
    icon: Share2,
    title: "Your social following",
    desc: "Share your unique salon referral code with your audience. Every client who signs up with your code is tracked to you, earning your salon additional rewards on top.",
  },
];

const STEPS = [
  { n: "01", icon: Store, title: "Register your salon", desc: "Create your salon profile in minutes. Open to salons worldwide with a minimum of 3 stylists." },
  { n: "02", icon: Link2, title: "Add your team", desc: "Register your stylists under your salon name. Each one gets a unique link or code to confirm they are associated with you." },
  { n: "03", icon: Share2, title: "Share your code", desc: "Promote your salon referral code across your social channels. Every sign up from your audience is credited to you." },
  { n: "04", icon: Wallet, title: "Earn automatically", desc: "Collect 7.5% of every stylist consultation plus referral rewards, paid out through the app. No admin, no chasing." },
];

const FEATURES = [
  { icon: TrendingUp, title: "A brand new revenue line", desc: "Monetise the downtime your salon already has. Consultations happen between appointments, at home or on quiet days, with no impact on your bookings." },
  { icon: BadgeCheck, title: "Your name, your team", desc: "Your stylists operate under your salon brand, keeping your reputation and client relationships firmly at the centre." },
  { icon: Globe, title: "A global client base", desc: "Your stylists can advise paying clients anywhere in the world through protected message consultations, capped at a 10 minute maximum." },
  { icon: HeartHandshake, title: "We complement, never compete", desc: "Senior Stylist is a digital add on, not a replacement. Clients still come to salons for the cut, colour and care. We simply add expert guidance in between." },
  { icon: ShieldCheck, title: "Vetted, protected, trusted", desc: "Under 20% of stylist applicants pass our verification. Consultations are capped and paced, so advice stays concise and professional." },
  { icon: Wallet, title: "Transparent, automatic payouts", desc: "Earnings are credited automatically and paid via Stripe Connect. Full visibility, no invoicing, no chargeback risk." },
];

const ELIGIBILITY = [
  { title: "Open worldwide", desc: "Salons anywhere in the world can register and start earning from their team and their following." },
  { title: "Minimum 3 stylists", desc: "You need at least 3 stylists registered under your salon name to activate your account." },
  { title: "Qualified, vetted team", desc: "Your stylists complete the same strict verification as all Senior Stylists (NVQ Level 2/3, 5+ years experience)." },
  { title: "Simple association", desc: "Stylists confirm their link to your salon with a code, so credit and commission always flow to the right place." },
];

const FAQS = [
  { q: "Are you trying to replace salons?", a: "Not at all. Our entire model is built to complement salons, not compete with them. Clients still visit you for the physical cut, colour and treatments. We simply give your stylists a way to monetise expert advice in the gaps, and give your salon a share of it." },
  { q: "How exactly does the 7.5% work?", a: "Every time one of your registered stylists completes a paid consultation on the app, your salon automatically earns 7.5% of that consultation fee. It is calculated and credited for you, with full visibility in your dashboard." },
  { q: "How do stylists get linked to my salon?", a: "When you register your stylists under your salon name, each one receives a unique link or code. They confirm it once and they are permanently associated with your salon, so your commission is tracked correctly." },
  { q: "How do the social referral rewards work?", a: "Your salon gets a referral code. Share it with your social audience, and whenever someone signs up using your code, that sign up is credited to your salon for additional rewards on top of your consultation commission." },
];

const reveal = {
  initial: { opacity: 0, y: 28 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-60px" },
  transition: { duration: 0.7, ease: [0.16, 1, 0.3, 1] },
};

function EarningsCalculator() {
  const [stylists, setStylists] = useState(6);
  const [perDay, setPerDay] = useState(3);
  const monthlyConsults = stylists * perDay * 30;
  const salonMonthly = monthlyConsults * AVG_FEE * SALON_RATE;

  return (
    <div className="rounded-3xl border border-stylist-gold/30 bg-white shadow-xl p-8 sm:p-10" data-testid="earnings-calculator">
      <div className="grid lg:grid-cols-2 gap-10 items-center">
        <div className="space-y-8">
          <div>
            <div className="flex items-center justify-between text-sm text-stylist-ink2">
              <span>Stylists in your salon</span>
              <span className="font-mono text-stylist-gold text-base">{stylists}</span>
            </div>
            <input type="range" min="3" max="20" value={stylists} data-testid="calc-stylists"
              onChange={(e) => setStylists(Number(e.target.value))}
              className="mt-3 w-full accent-[#C59B27] cursor-pointer" />
          </div>
          <div>
            <div className="flex items-center justify-between text-sm text-stylist-ink2">
              <span>Consultations per stylist, per day</span>
              <span className="font-mono text-stylist-gold text-base">{perDay}</span>
            </div>
            <input type="range" min="1" max="6" value={perDay} data-testid="calc-perday"
              onChange={(e) => setPerDay(Number(e.target.value))}
              className="mt-3 w-full accent-[#C59B27] cursor-pointer" />
          </div>
          <p className="text-xs text-stylist-muted">
            Based on a standard verified consultation fee. Actual figures vary by stylist tier, and
            most consultations last just a few minutes.
          </p>
        </div>

        <div className="rounded-2xl bg-stylist-ink text-white p-8 text-center">
          <p className="font-mono text-[0.68rem] uppercase tracking-[0.24em] text-stylist-gold">
            Your salon could earn
          </p>
          <p className="mt-3 font-serif text-5xl sm:text-6xl text-stylist-gold tabular-nums" data-testid="calc-result">
            {gbp(salonMonthly)}
          </p>
          <p className="text-sm text-white/50">per month from consultations</p>
          <div className="mt-6 pt-6 border-t border-white/10 grid grid-cols-2 gap-4 text-left">
            <div>
              <p className="font-serif text-2xl">{monthlyConsults.toLocaleString("en-GB")}</p>
              <p className="text-xs text-white/45">team consultations / month</p>
            </div>
            <div>
              <p className="font-serif text-2xl text-stylist-gold">7.5%</p>
              <p className="text-xs text-white/45">salon commission</p>
            </div>
          </div>
          <p className="mt-5 text-xs text-white/50">Plus extra rewards from every social referral sign up.</p>
        </div>
      </div>
    </div>
  );
}

export default function SalonLanding() {
  const [showBar, setShowBar] = useState(false);

  useEffect(() => {
    document.title = "Partner Your Salon | Senior Stylist";
    window.scrollTo(0, 0);
    const onScroll = () => setShowBar(window.scrollY > 640);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

  const notOpen = () =>
    toast("We are not onboarding salons at the moment", {
      description: "We will keep you posted. Please check back soon.",
    });

  return (
    <div className="bg-stylist-bg text-stylist-ink" data-testid="salon-landing-page">
      <header className="sticky top-0 z-40 backdrop-blur bg-stylist-bg/80 border-b border-stylist-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5" data-testid="salon-logo">
            <span className="w-9 h-9 rounded-full bg-white border border-stylist-border flex items-center justify-center">
              <img src="/logo.png" alt="Senior Stylist" className="w-6 h-6 object-contain" />
            </span>
            <span className="font-serif text-lg">Senior Stylist</span>
          </Link>
          <button onClick={notOpen} data-testid="header-cta"
            className="rounded-full bg-stylist-ink text-white px-5 py-2.5 text-xs font-medium hover:bg-stylist-gold transition-colors">
            Register your salon
          </button>
        </div>
      </header>

      {/* HERO */}
      <section className="relative overflow-hidden pt-16 pb-20 md:pt-24 md:pb-28">
        <div className="absolute inset-0 pointer-events-none opacity-60"
          style={{ background: "radial-gradient(ellipse at 80% 0%, rgba(197,155,39,0.12), transparent 55%)" }} />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-6">
            <p className="font-mono text-[0.62rem] sm:text-[0.68rem] uppercase tracking-[0.24em] text-stylist-gold">
              For salons · two revenue streams · zero disruption
            </p>
            <h1 className="mt-6 font-serif font-light text-4xl sm:text-5xl lg:text-6xl leading-[1.04] tracking-tight text-stylist-ink">
              Digitalise and monetise your salon's two biggest assets,{" "}
              <span className="italic text-stylist-gold">your stylists and your following.</span>
            </h1>
            <p className="mt-6 text-base md:text-lg text-stylist-ink2 max-w-xl leading-relaxed">
              Senior Stylist turns your team's expertise and your social audience into recurring
              income. Earn 7.5% of every consultation your stylists deliver, plus rewards from every
              sign up your following brings in. All without changing a thing about how your salon runs.
            </p>
            <div className="mt-9 flex flex-col sm:flex-row items-start sm:items-center gap-4">
              <button onClick={notOpen} data-testid="hero-cta"
                className="group inline-flex items-center gap-2 rounded-full bg-stylist-ink text-white px-8 py-4 text-sm font-medium hover:bg-stylist-gold transition-colors duration-300">
                Register your salon
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </button>
              <button onClick={() => scrollTo("calculator")} data-testid="hero-see-how"
                className="inline-flex items-center gap-2 text-sm text-stylist-ink2 hover:text-stylist-ink ss-link-underline">
                <Play className="w-4 h-4 text-stylist-gold" />
                See what your salon could earn
              </button>
            </div>
            <ul className="mt-10 space-y-2.5">
              {TRUST_BADGES.map((b) => (
                <li key={b} className="flex items-start gap-3 text-sm text-stylist-ink">
                  <span className="mt-0.5 w-5 h-5 shrink-0 rounded-full bg-stylist-gold/15 flex items-center justify-center">
                    <Check className="w-3 h-3 text-stylist-gold" />
                  </span>
                  {b}
                </li>
              ))}
            </ul>
          </div>

          {/* salon earnings mockup */}
          <div className="lg:col-span-6">
            <div className="relative mx-auto max-w-[420px]">
              <div className="rounded-[28px] border border-stylist-border bg-white shadow-2xl p-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2.5">
                    <span className="w-10 h-10 rounded-full bg-stylist-gold/15 flex items-center justify-center">
                      <Store className="w-5 h-5 text-stylist-gold" />
                    </span>
                    <div className="leading-tight">
                      <p className="text-sm font-medium text-stylist-ink">The Mayfair Rooms</p>
                      <p className="text-[10px] font-mono uppercase tracking-wider text-stylist-muted">Salon partner</p>
                    </div>
                  </div>
                  <span className="rounded-full bg-stylist-gold/15 text-stylist-gold text-[10px] font-mono uppercase tracking-wider px-3 py-1.5">7.5%</span>
                </div>
                <div className="mt-6 rounded-2xl bg-stylist-bg2 p-5 text-center">
                  <p className="text-[10px] font-mono uppercase tracking-widest text-stylist-muted">This month</p>
                  <p className="mt-1 font-serif text-4xl text-stylist-ink">£1,498</p>
                  <p className="text-xs text-stylist-muted">salon commission earned</p>
                </div>
                <div className="mt-4 grid grid-cols-2 gap-3">
                  <div className="rounded-xl border border-stylist-border p-3">
                    <p className="font-serif text-2xl text-stylist-ink">8</p>
                    <p className="text-[11px] text-stylist-muted">stylists active</p>
                  </div>
                  <div className="rounded-xl border border-stylist-border p-3">
                    <p className="font-serif text-2xl text-stylist-gold">54</p>
                    <p className="text-[11px] text-stylist-muted">referral sign ups</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* TWO REVENUE STREAMS */}
      <section className="py-16 md:py-24 bg-stylist-bg2">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16">
          <motion.div {...reveal} className="text-center max-w-2xl mx-auto mb-14">
            <span className="eyebrow">Two ways to earn</span>
            <h2 className="mt-3 font-serif text-3xl sm:text-4xl lg:text-5xl">
              One partnership, two income streams
            </h2>
          </motion.div>
          <div className="grid md:grid-cols-2 gap-6 lg:gap-8">
            {STREAMS.map((s, i) => (
              <motion.div key={s.title} {...reveal} transition={{ ...reveal.transition, delay: i * 0.1 }}
                className="rounded-2xl border border-stylist-border bg-white p-8 hover:shadow-lg transition-shadow duration-500"
                data-testid={`stream-${i + 1}`}>
                <div className="w-12 h-12 rounded-full bg-stylist-gold/12 flex items-center justify-center">
                  <s.icon className="w-6 h-6 text-stylist-gold" />
                </div>
                <h3 className="mt-5 font-serif text-2xl">{s.title}</h3>
                <p className="mt-3 text-sm text-stylist-ink2 leading-relaxed">{s.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CALCULATOR */}
      <section id="calculator" className="py-16 md:py-24">
        <div className="max-w-5xl mx-auto px-4 sm:px-8 lg:px-16">
          <motion.div {...reveal} className="text-center max-w-2xl mx-auto mb-12">
            <span className="eyebrow">Revenue estimator</span>
            <h2 className="mt-3 font-serif text-3xl sm:text-4xl lg:text-5xl">See what your salon could earn</h2>
            <p className="mt-4 text-stylist-ink2">
              Drag the sliders to estimate your salon's monthly commission from your team's consultations.
            </p>
          </motion.div>
          <motion.div {...reveal}>
            <EarningsCalculator />
          </motion.div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="py-16 md:py-24 bg-stylist-bg2">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16">
          <motion.div {...reveal} className="text-center max-w-2xl mx-auto mb-14">
            <span className="eyebrow">Simple setup</span>
            <h2 className="mt-3 font-serif text-3xl sm:text-4xl lg:text-5xl">Four steps to a new revenue line</h2>
          </motion.div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {STEPS.map((s, i) => (
              <motion.div key={s.title} {...reveal} transition={{ ...reveal.transition, delay: i * 0.08 }}
                className="relative bg-white border border-stylist-border rounded-2xl p-6 hover:border-stylist-gold/40 hover:shadow-lg transition-all duration-500"
                data-testid={`step-${s.n}`}>
                <div className="flex items-center justify-between">
                  <span className="font-mono text-xs tracking-[0.3em] text-stylist-muted">{s.n}</span>
                  <div className="w-10 h-10 rounded-full bg-stylist-gold/15 flex items-center justify-center">
                    <s.icon className="w-5 h-5 text-stylist-gold" />
                  </div>
                </div>
                <h3 className="mt-4 font-serif text-lg leading-snug">{s.title}</h3>
                <p className="mt-2 text-sm text-stylist-ink2 leading-relaxed">{s.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* COMPLEMENT NOT REPLACE */}
      <section className="py-16 md:py-24">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <motion.div {...reveal}>
            <span className="eyebrow">Our promise to salons</span>
            <h2 className="mt-4 font-serif font-light text-3xl sm:text-4xl lg:text-5xl leading-tight">
              We are not here to replace salons. We are here to{" "}
              <span className="italic text-stylist-gold">complement them.</span>
            </h2>
            <p className="mt-6 text-lg text-stylist-ink2 leading-relaxed">
              Your clients will always come to you for the cut, the colour and the care that only
              happens in the chair. Senior Stylist simply adds an expert digital layer in between
              visits, keeping your salon at the heart of the relationship while opening up brand new
              income from expertise you already have.
            </p>
          </motion.div>
        </div>
      </section>

      {/* FEATURES */}
      <section className="py-16 md:py-24 bg-stylist-bg2">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16">
          <motion.div {...reveal} className="max-w-2xl mb-14">
            <span className="eyebrow">Why salons partner with us</span>
            <h2 className="mt-3 font-serif text-3xl sm:text-4xl lg:text-5xl">Built to grow your business</h2>
          </motion.div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {FEATURES.map((f, i) => (
              <motion.div key={f.title} {...reveal} transition={{ ...reveal.transition, delay: (i % 3) * 0.08 }}
                className="rounded-2xl border border-stylist-border bg-white p-7 hover:shadow-lg transition-shadow duration-500"
                data-testid={`feature-${i + 1}`}>
                <div className="w-12 h-12 rounded-full bg-stylist-gold/12 flex items-center justify-center">
                  <f.icon className="w-6 h-6 text-stylist-gold" />
                </div>
                <h3 className="mt-5 font-serif text-xl leading-snug">{f.title}</h3>
                <p className="mt-3 text-sm text-stylist-ink2 leading-relaxed">{f.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ELIGIBILITY */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16">
          <div className="grid lg:grid-cols-12 gap-12 items-start">
            <motion.div {...reveal} className="lg:col-span-4">
              <span className="eyebrow">Who can join</span>
              <h2 className="mt-3 font-serif text-3xl sm:text-4xl lg:text-5xl leading-tight">Eligibility</h2>
              <div className="mt-8 rounded-2xl bg-stylist-ink text-white p-7">
                <span className="font-serif text-4xl text-stylist-gold">3+</span>
                <p className="mt-2 text-sm text-white/60">stylists needed to activate your salon, anywhere in the world.</p>
              </div>
            </motion.div>
            <div className="lg:col-span-8 grid gap-5">
              {ELIGIBILITY.map((e, i) => (
                <motion.div key={e.title} {...reveal} transition={{ ...reveal.transition, delay: i * 0.07 }}
                  className="flex items-start gap-5 rounded-2xl border border-stylist-border bg-white p-7"
                  data-testid={`eligibility-${i + 1}`}>
                  <span className="mt-0.5 w-6 h-6 shrink-0 rounded-full bg-stylist-gold/20 flex items-center justify-center">
                    <Check className="w-3.5 h-3.5 text-stylist-gold" />
                  </span>
                  <div>
                    <h3 className="font-serif text-xl">{e.title}</h3>
                    <p className="mt-1.5 text-sm text-stylist-ink2 leading-relaxed">{e.desc}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 md:py-24 bg-stylist-bg2">
        <div className="max-w-3xl mx-auto px-4 sm:px-8">
          <motion.div {...reveal} className="text-center mb-10">
            <span className="eyebrow">Good to know</span>
            <h2 className="mt-3 font-serif text-3xl sm:text-4xl lg:text-5xl">Salon questions, answered</h2>
          </motion.div>
          <motion.div {...reveal}>
            <Accordion type="single" collapsible className="w-full" data-testid="faq-accordion">
              {FAQS.map((f, i) => (
                <AccordionItem key={f.q} value={`faq-${i}`} className="border-stylist-border">
                  <AccordionTrigger className="font-serif text-lg text-stylist-ink hover:no-underline py-5 text-left">
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-stylist-ink2 leading-relaxed text-sm">
                    {f.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </motion.div>
        </div>
      </section>

      {/* BOTTOM CTA */}
      <section className="py-20 md:py-28 bg-stylist-ink text-white relative overflow-hidden">
        <div className="absolute inset-0 ss-grain opacity-[0.06] pointer-events-none" />
        <div className="absolute inset-0 opacity-50 pointer-events-none"
          style={{ background: "radial-gradient(ellipse at 50% 120%, rgba(197,155,39,0.22), transparent 60%)" }} />
        <motion.div {...reveal} className="relative max-w-3xl mx-auto px-6 text-center">
          <h2 className="font-serif font-light text-4xl sm:text-5xl lg:text-6xl leading-[1.05]">
            Turn your salon's expertise into a new revenue stream.
          </h2>
          <p className="mt-6 text-lg text-white/60 max-w-xl mx-auto">
            Register in minutes, add your team, and start earning from every consultation and every
            referral. No disruption, no risk, pure upside.
          </p>
          <div className="mt-10 flex justify-center">
            <button onClick={notOpen} data-testid="bottom-cta"
              className="group inline-flex items-center gap-2 rounded-full bg-stylist-gold text-stylist-ink px-9 py-4 text-sm font-medium hover:bg-white transition-colors duration-300">
              Register your salon
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </button>
          </div>
          <p className="mt-10 text-[11px] font-mono uppercase tracking-[0.22em] text-white/40">
            Senior Stylist Ltd. · Worldwide salon partnerships · 7.5% consultation commission
          </p>
        </motion.div>
      </section>

      {/* STICKY BAR */}
      <div className={"fixed bottom-0 inset-x-0 z-40 transition-transform duration-500 " + (showBar ? "translate-y-0" : "translate-y-full")}
        data-testid="sticky-cta-bar">
        <div className="mx-auto max-w-3xl m-3 rounded-2xl bg-white border border-stylist-border shadow-2xl px-5 py-3 flex items-center justify-between gap-4">
          <div className="leading-tight">
            <p className="text-sm font-medium text-stylist-ink">Partner your salon</p>
            <p className="text-xs text-stylist-muted">7.5% of every consultation · plus referral rewards</p>
          </div>
          <button onClick={notOpen} data-testid="sticky-cta-button"
            className="shrink-0 rounded-full bg-stylist-ink text-white px-6 py-3 text-sm font-medium hover:bg-stylist-gold transition-colors">
            Register now
          </button>
        </div>
      </div>
    </div>
  );
}
